import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { contactService } from '../../services/firebaseService';

interface ContactSubmission {
  id?: string;
  name: string;
  email: string;
  subject?: string;
  message: string;
  submittedAt?: any;
}

const ContactSubmissionsManager: React.FC = () => {
  const { logout } = useAuth();
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const data = await contactService.getContactSubmissions();
      setSubmissions(data as ContactSubmission[]);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Unknown';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    return searchTerm === '' || 
      sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sub.subject && sub.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      sub.message.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <h3>Admin Panel</h3>
        <nav>
          <ul>
            <li><Link to="/admin">Dashboard</Link></li>
            <li><Link to="/admin/content">Content</Link></li>
            <li><Link to="/admin/board">Board Members</Link></li>
            <li><Link to="/admin/programs">Programs</Link></li>
            <li><Link to="/admin/events">Events</Link></li>
            <li><Link to="/admin/volunteers">Volunteer Applications</Link></li>
            <li><Link to="/admin/contact">Contact Submissions</Link></li>
            <li><button onClick={logout}>Logout</button></li>
          </ul>
        </nav>
      </div>
      <div className="admin-content">
        <h1>Contact Form Submissions</h1>
        <p>View messages submitted through the Contact Us form.</p>

        <div style={{ marginBottom: '2rem' }}>
          <input
            type="text"
            placeholder="Search by name, email, subject, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '500px',
              padding: '0.5rem',
              fontSize: '0.9em',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No contact submissions found{searchTerm ? ` matching "${searchTerm}"` : ''}.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredSubmissions.map(sub => (
              <div key={sub.id} className="card" style={{ padding: '1.5rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <h3 style={{ margin: '0 0 0.5rem 0' }}>{sub.name}</h3>
                  <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.25rem' }}>
                    <strong>Email:</strong> <a href={`mailto:${sub.email}`}>{sub.email}</a>
                  </div>
                  {sub.subject && (
                    <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.25rem' }}>
                      <strong>Subject:</strong> {sub.subject}
                    </div>
                  )}
                  <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.5rem' }}>
                    <strong>Submitted:</strong> {formatDate(sub.submittedAt)}
                  </div>
                </div>
                <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <strong>Message:</strong>
                  <p style={{ margin: '0.5rem 0 0 0', whiteSpace: 'pre-wrap' }}>{sub.message}</p>
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <a href={`mailto:${sub.email}${sub.subject ? `?subject=Re: ${encodeURIComponent(sub.subject)}` : ''}`} className="btn btn-primary" style={{ fontSize: '0.85em' }}>
                    Reply via Email
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSubmissionsManager;

